import config from "environment";
import { getRequest, postRequest } from "./client";
import {
  AIFormFillResponse,
  AIResponseSave,
  AskContract,
  RecentAnswer,
  SnippetSimilarity,
} from "winslow-core";
import { AskSnippetResponse } from "winslow-core";
import {
  apiAISaveResponsePayload,
  apiAIAskSnippetPayload,
} from "./AIService.types";
import { AgentNewThreadMessage } from "../components/agent-chat/types";

const { baseApi } = config;

export type AIResponse = {
  answer: string;
  sources: string[];
  linkRecordIds: string[];
};

export async function apiAIAskQuestion(
  payload: AskContract["Body"]
): Promise<AskContract["Reply"]["data"] | null> {
  const fetchURL = `${baseApi}/ai/ask`;
  return await postRequest(fetchURL, payload, "API/apiAIAskQuestion");
}

export async function apiAIAskOpenQuestion(
  workspaceId: string,
  question: string
): Promise<string | null> {
  const fetchURL = `${baseApi}/ai/ask-open-question`;
  return await postRequest(
    fetchURL,
    { workspaceId, question },
    "API/apiAIAskOpenQuestion"
  );
}

export async function apiAIAskReportingQuestion(
  workspaceId: string,
  question: string
): Promise<string | null> {
  const fetchURL = `${baseApi}/ai/ask-reporting-question`;
  return await postRequest(
    fetchURL,
    { workspaceId, question },
    "API/apiAIAskReportingQuestion"
  );
}

export async function apiAIAskUIQuestion(
  workspaceId: string,
  question: string
): Promise<string | null> {
  const fetchURL = `${baseApi}/ai/ask-ui-question`;
  return await postRequest(
    fetchURL,
    { workspaceId, question },
    "API/apiAIAskUIQuestion"
  );
}

export async function apiAIAskAssetQuestion(
  workspaceId: string,
  employeeRecordId: string,
  question: string
): Promise<string | null> {
  const fetchURL = `${baseApi}/ai/ask-asset-question`;
  return await postRequest(
    fetchURL,
    { workspaceId, employeeRecordId, question },
    "API/apiAIAskAssetQuestion"
  );
}

export async function apiAIAskFormFillQuestion(
  workspaceId: string,
  question: string
): Promise<AIFormFillResponse | null> {
  const fetchURL = `${baseApi}/ai/ask-form-fill-question`;
  return await postRequest(
    fetchURL,
    { workspaceId, question },
    "API/apiAIAskFormFillQuestion"
  );
}

export async function apiAIAskSnippet(
  payload: apiAIAskSnippetPayload
): Promise<AskSnippetResponse | null> {
  const fetchURL = `${baseApi}/ai/ask-snippet`;
  console.log("[apiAIAskSnippet] payload: ", payload);
  return await postRequest(fetchURL, payload, "API/apiAIAskSnippet");
}

export async function apiGetSnippets(
  payload: apiAIAskSnippetPayload
): Promise<SnippetSimilarity[] | null> {
  const fetchURL = `${baseApi}/ai/get-snippets`;
  console.log("[apiAIAskSnippet] payload: ", payload);
  return await postRequest(fetchURL, payload, "API/apiAIAskSnippet");
}

export async function apiGenerateSnippet(
  payload: apiAIAskSnippetPayload
): Promise<AskSnippetResponse | null> {
  const fetchURL = `${baseApi}/ai/generate-snippet`;
  console.log("[apiAIAskSnippet] payload: ", payload);
  return await postRequest(fetchURL, payload, "API/apiGenerateSnippet");
}

export async function apiAISaveResponse(
  payload: apiAISaveResponsePayload
): Promise<AIResponseSave | null> {
  const fetchURL = `${baseApi}/ai/save-response`;
  console.log("[AIService] payload: ", payload);
  return await postRequest(fetchURL, payload, "API/apiAISaveResponse");
}

export async function apiAISaveAgentResponse(
  payload: apiAISaveResponsePayload
): Promise<AIResponseSave | null> {
  const fetchURL = `${baseApi}/ai/save-agent-response`;
  console.log("[AIService] payload: ", payload);
  return await postRequest(fetchURL, payload, "API/apiAISaveAgentResponse");
}

// for a user in  workspace
export async function apiAIGetResponseSaves(
  userId: string,
  workspaceId: string
): Promise<AIResponseSave[] | null> {
  const fetchURL = `${baseApi}/ai/saved-responses/${userId}/${workspaceId}`;
  return await getRequest(fetchURL, "API/apiAIGetResponseSaves");
}

export async function apiGetUserRecentAnswers(
  userId: string,
  workspaceId: string
): Promise<RecentAnswer[] | null> {
  const fetchURL = `${baseApi}/ai/recent-answers/${userId}/${workspaceId}`;
  return await getRequest(fetchURL, "API/apiUserRecentAnswersSaves");
}

export async function apiAIGetResponseSavesForUserInCollection(
  userId: string,
  collectionId: string
): Promise<AIResponseSave[] | null> {
  const fetchURL = `${baseApi}/ai/saved-responses-for-user-in-collection/${userId}/${collectionId}`;
  return await getRequest(
    fetchURL,
    "API/apiAIGetResponseSavesForUserInCollection"
  );
}

export async function apiGetWorkspaceAnswers(
  workspaceId: string,
  userId: string
): Promise<RecentAnswer[] | null> {
  const fetchURL = `${baseApi}/workspaces/answers/${workspaceId}/${userId}`;
  return await getRequest(fetchURL, "API/apiGetWorkspaceAnswers");
}

export async function apiAIGetResponseSavesForCollection(
  collectionId: string
): Promise<AIResponseSave[] | null> {
  const fetchURL = `${baseApi}/ai/saved-responses-for-collection/${collectionId}`;
  return await getRequest(fetchURL, "API/apiAIGetResponseSavesForCollection");
}

export async function apiAIGetResponseSavesVisibleToUserInWorkspace(
  userId: string,
  workspaceId: string
): Promise<AIResponseSave[] | null> {
  const fetchURL = `${baseApi}/ai/saved-responses-visible-to-user-in-workspace/${userId}/${workspaceId}`;
  return await getRequest(
    fetchURL,
    "API/apiAIGetResponseSavesVisibleToUserInWorkspace"
  );
}

export async function apiGetRecentAnswersVisibleToUserInWorkspace(
  userId: string,
  workspaceId: string
): Promise<RecentAnswer[] | null> {
  const fetchURL = `${baseApi}/ai/recent-answers-visible-to-user-in-workspace/${userId}/${workspaceId}`;
  return await getRequest(
    fetchURL,
    "API/apiGetRecentAnswersVisibleToUserInWorkspace"
  );
}

export async function apiAIUpdateResponseFeedback(
  responseId: string,
  feedback: "up" | "down"
): Promise<AIResponseSave | null> {
  const fetchURL = `${baseApi}/ai/update-feedback/${responseId}/${feedback}`;
  return await getRequest(fetchURL, "API/apiAIUpdateResponseFeedback");
}

export async function aiAIGetResponseSave(
  aiResponseSaveId: string
): Promise<AIResponseSave | null> {
  const fetchURL = `${baseApi}/ai/saved-response/${aiResponseSaveId}`;
  return await getRequest(fetchURL, "API/aiAIGetResponseSave");
}

export async function apiGetConversationHistory(
  userId: string,
  collectionId: string
): Promise<any[] | null> {
  const fetchURL = `${baseApi}/ai/get-conversation-history/${userId}/${collectionId}`;
  return await getRequest(fetchURL, "API/apiGetConversationHistory");
}

export async function apiStartNewThread(
  userId: string,
  collectionId: string,
  personaId: string | null,
  employeeId: string | null
): Promise<AgentNewThreadMessage | null> {
  const fetchURL = `${baseApi}/ai/start-new-thread`;
  return await postRequest(
    fetchURL,
    { userId, collectionId, personaId, employeeId },
    "API/apiStartNewThread"
  );
}
